import { CheckCircle, WarningAmber } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { UploadType } from 'further-types/files';
import FileDrop from 'further-ui/components/FileDrop';
import { Column, Row } from 'further-ui/components/Layout';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import { useFlag } from '@reflag/react-sdk';
import api from 'lib/trpcClient';
import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { useAiFormFill } from '../AiFormFillContext';
import { useAiAgent } from 'contexts/AiAgentContext';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import { FormValues } from '../FormWrapper';
import { PendingInvoice } from 'components/AiAgent';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';

const InnerRow = styled(Row)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
}));

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

type AiMediaType = 'application/pdf';

const getAiMediaType = (file: File): AiMediaType | null => {
  const { type } = file;
  if (type === 'application/pdf') {
    return type;
  }
  return null;
};

type Props = {
  vehicleId: string;
  disabled?: boolean;
};

const InvoiceUploadSection: FC<Props> = ({ vehicleId, disabled }) => {
  const { control, watch, setValue } = useFormContext<FormValues>();
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();
  const { isEnabled: showAiAgent } = useFlag('show-experimental-ai-agent');
  const {
    applyFormFill,
    hasAppliedFormFill,
    hints,
    isUploadingInvoice,
    setIsUploadingInvoice,
    isAiScanning,
    setIsAiScanning,
  } = useAiFormFill();
  const [aiScanComplete, setAiScanComplete] = useState(hasAppliedFormFill);
  const [aiScanError, setAiScanError] = useState<string | null>(null);

  const { pushNotification } = useAiAgent();
  const chatMutation = api.aiAgent.chatFormFill.useMutation();
  const history = useHistory();
  const location = useLocation<{ pendingInvoice?: PendingInvoice }>();
  const pendingInvoice = location.state?.pendingInvoice;
  const pendingInvoicePath = pendingInvoice?.uploadedPath;

  type FormFillData = NonNullable<
    Awaited<ReturnType<typeof chatMutation.mutateAsync>>['formFill']
  >;

  const hasLowConfidenceFields = Object.entries(hints)
    .filter(([label]) => label !== 'apportionmentMode')
    .some(([, hint]) => hint && hint.confidence !== 'high');

  const startAiScan = async (pending: {
    base64: string;
    mediaType: 'application/pdf';
    fileName: string;
  }) => {
    setIsAiScanning(true);
    setAiScanComplete(false);
    setAiScanError(null);

    chatMutation
      .mutateAsync({
        vehicleId,
        file: {
          base64: pending.base64,
          mediaType: pending.mediaType,
          fileName: pending.fileName,
        },
      })
      .then((response) => {
        if (response.formFill) {
          applyFormFill(response.formFill);
          pushNotification(buildNotification(response.formFill));
        }
        setAiScanComplete(true);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'AI scan not possible';
        setAiScanError(message);
      })
      .finally(() => {
        setIsAiScanning(false);
      });
  };

  const buildNotification = (formFill: FormFillData): string => {
    const category = formFill.category?.value;
    const categoryLabel =
      category === IncomeOrExpenseType.Income
        ? 'Income'
        : category === IncomeOrExpenseType.Expense
        ? 'Expense'
        : null;
    const state = hasLowConfidenceFields ? 'partially populated' : 'populated';
    return categoryLabel
      ? `${categoryLabel} fields have been ${state}`
      : `Fields have been ${state}`;
  };

  useEffect(() => {
    const pending = pendingInvoice;
    if (!pending || !showAiAgent || hasAppliedFormFill || disabled) return;

    setValue('invoiceFile', {
      path: pending.uploadedPath,
      name: pending.uploadedName,
      preview: pending.uploadedPreview,
    });

    // Clear pendingInvoice from history state so back-navigation doesn't re-trigger
    history.replace(location.pathname + location.search, {
      ...location.state,
      pendingInvoice: undefined,
    });

    startAiScan(pending);
  }, [pendingInvoicePath]);

  const invoiceFile = watch('invoiceFile');

  const handleInvoiceFileDrop = async (
    files: Array<{ fileName: string; previewUri: string; fileData: File }>,
    onChange: (
      value: { path: string; name: string; preview: string } | null,
    ) => void,
  ) => {
    const rawFile = files?.[0]?.fileData;
    if (!rawFile) return;

    setIsUploadingInvoice(true);
    setAiScanComplete(false);

    try {
      const mediaType = showAiAgent ? getAiMediaType(rawFile) : null;
      const [uploadResult, base64] = await Promise.all([
        uploadFile(rawFile, UploadType.LpTransactionAttachments),
        mediaType ? fileToBase64(rawFile) : Promise.resolve(null),
      ]);

      if (uploadResult.data) {
        onChange({
          path: uploadResult.data.filePath,
          name: uploadResult.data.name ?? rawFile.name,
          preview: uploadResult.data.url,
        });
      } else {
        error(uploadResult.error.message);
        return;
      }

      if (showAiAgent && base64 && mediaType) {
        setIsUploadingInvoice(false);
        startAiScan({ base64, mediaType, fileName: rawFile.name });
      }
    } finally {
      setIsUploadingInvoice(false);
    }
  };

  return (
    <Controller
      name="invoiceFile"
      control={control}
      render={({ field: { onChange } }) => (
        <FileDrop
          inputTitle="Upload invoice"
          maxFiles={1}
          spacing={4}
          hideDropzoneWhenPopulated
          disabled={disabled}
          files={
            invoiceFile
              ? [
                  {
                    fileName: invoiceFile.name,
                    previewUri: invoiceFile.preview,
                  },
                ]
              : isUploadingInvoice
              ? [{ fileName: '', isUploading: true }]
              : []
          }
          onDeleteFile={() => {
            onChange(null);
            setAiScanComplete(false);
            setAiScanError(null);
          }}
          onDropFile={(files) => handleInvoiceFileDrop(files, onChange)}
        >
          {showAiAgent && (isAiScanning || aiScanComplete || !!aiScanError) ? (
            <InnerRow justify="space-between" centered>
              <Row centered spacing="sm">
                <GradientAiIcon size={20} style={{ flexShrink: 0 }} />
                {isAiScanning ? (
                  <Typography variant="body2" color="text.secondary">
                    Scanning upload...
                  </Typography>
                ) : aiScanError ? (
                  <Typography variant="body2" color="text.secondary">
                    {aiScanError}
                  </Typography>
                ) : (
                  <Column>
                    <Typography variant="h6">
                      {hasLowConfidenceFields
                        ? 'Fields have been partially populated'
                        : 'Fields have been populated'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasLowConfidenceFields
                        ? 'Please input the remaining fields and submit.'
                        : 'Feel free to check the below and submit.'}
                    </Typography>
                  </Column>
                )}
              </Row>
              {isAiScanning ? (
                <CircularProgress size={18} sx={{ flexShrink: 0 }} />
              ) : aiScanError ? (
                <WarningAmber
                  sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }}
                />
              ) : (
                <CheckCircle
                  sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }}
                />
              )}
            </InnerRow>
          ) : null}
        </FileDrop>
      )}
    />
  );
};

export default InvoiceUploadSection;
